/**
 * Campaign Details hook.
 *
 * `useCampaignDetails` — in-component hook that fetches full campaign details
 * with loading state. Renders the page immediately with a spinner while data loads.
 *
 * The hook accepts a `campaignId`, `version` (both as strings from URL params),
 * and `campaignType` (enum from URL params, required for PAAM auth)
 * and validates them before making the API call.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { CampaignData } from 'src/@types/valueMatching/campaign';
import { CampaignType } from 'src/@types/valueMatching/enums';
import { fetchCampaignDetails } from 'src/accessors/valueMatchingAccessor';
import { logError } from 'src/utils/monitoring';

/** Generic fallback message shown to users when an API error occurs */
const GENERIC_ERROR_MESSAGE = 'Failed to load campaign details. Please try again later.';

/** Shape of the data returned by useCampaignDetails */
export interface UseCampaignDetailsResult {
    /** Full campaign data (null until loaded) */
    campaignData: CampaignData | null;
    /** Whether the fetch is in progress */
    loading: boolean;
    /** Error message (null on success) */
    error: string | null;
    /**
     * Triggers a re-fetch of the campaign details using the current
     * `campaignId`, `version`, and `campaignType`. Stable reference across
     * renders so consumers can pass it as a dependency without re-triggering
     * effects.
     */
    refetch: () => void;
}

/**
 * Returns true when `value` represents a positive integer (1, 2, 3, …).
 * Rejects empty strings, non-numeric strings, zero, negatives, and decimals.
 */
function isPositiveInteger(value: string): boolean {
    const num = Number(value);
    return Number.isInteger(num) && num > 0;
}

/**
 * Hook that fetches campaign details for a given campaignId and version.
 * Renders the page immediately and shows a loading spinner while data loads.
 *
 * @param campaignId   - Campaign identifier from URL params. Pass `undefined` if not yet available.
 * @param version      - Version string from URL params. Must be a positive integer string.
 * @param campaignType - Campaign type enum from URL params. Required for PAAM auth on the BFF.
 * @returns Campaign data, loading state, and error state
 *
 * @example
 * ```tsx
 * const { campaignId, version } = useParams();
 * const campaignType = SLUG_TO_CAMPAIGN_TYPE[slug];
 * const { campaignData, loading, error } = useCampaignDetails(campaignId, version, campaignType);
 * ```
 */
export function useCampaignDetails(
    campaignId: string | undefined,
    version: string | undefined,
    campaignType: CampaignType | undefined,
): UseCampaignDetailsResult {
    const safeCampaignId = (campaignId ?? '').trim();
    const safeVersion = (version ?? '').trim();

    const paramsValid = safeCampaignId.length > 0 && isPositiveInteger(safeVersion) && campaignType !== undefined;

    const [campaignData, setCampaignData] = useState<CampaignData | null>(null);
    // Only show loading spinner when we have valid params to fetch.
    const [loading, setLoading] = useState(paramsValid);
    const [error, setError] = useState<string | null>(null);
    // Incrementing this counter re-triggers the fetch effect below, giving
    // consumers a manual refetch capability without changing the public API.
    const [refreshCounter, setRefreshCounter] = useState(0);

    // Use ref to track the AbortController for proper request cancellation on cleanup
    const abortControllerRef = useRef<AbortController | null>(null);

    const refetch = useCallback(() => {
        setRefreshCounter((c) => c + 1);
    }, []);

    useEffect(() => {
        if (!paramsValid) {
            setError('Invalid campaign parameters. Campaign ID must be non-empty and version must be a positive integer.');
            setLoading(false);
            setCampaignData(null);
            return;
        }

        // Cancel any in-flight request from a previous render
        abortControllerRef.current?.abort();
        const abortController = new AbortController();
        abortControllerRef.current = abortController;

        let cancelled = false;

        const loadDetails = async () => {
            setLoading(true);
            setError(null);
            setCampaignData(null); // clear stale data from previous params

            try {
                const response = await fetchCampaignDetails({
                    campaignId: safeCampaignId,
                    version: Number(safeVersion),
                    campaignType: campaignType!,
                    signal: abortController.signal,
                });
                if (!cancelled) {
                    const data = response?.campaignData;
                    if (!data) {
                        throw new Error('Campaign data missing from API response');
                    }
                    setCampaignData(data);
                }
            } catch (err) {
                if (!cancelled) {
                    setCampaignData(null);
                    const message = err instanceof Error ? err.message : String(err);
                    logError(message, { methodName: 'useCampaignDetails' });
                    setError(GENERIC_ERROR_MESSAGE);
                }
            } finally {
                if (!cancelled) {
                    setLoading(false);
                }
            }
        };

        loadDetails();

        return () => {
            cancelled = true;
            abortController.abort();
        };
    }, [safeCampaignId, safeVersion, campaignType, paramsValid, refreshCounter]);

    return { campaignData, loading, error, refetch };
}
